import { useCallback } from 'react';
import { useEffect } from 'react';
import { useRef } from 'react';
import { useState } from 'react';

type Options = {
  capacity?: number; // max snapshots kept
  debounceMs?: number; // 0 = no debouncing (default)
};

/* ---------- public contract (same shape, plus `ready`) ---------- */
export interface UndoableState<T> {
  state: T; // never null
  set: (v: T | ((p: T | undefined) => T)) => void;
  undo: () => void;
  redo: () => void;
  canUndo: boolean;
  canRedo: boolean;
  reset: (nextInitial?: T) => void;
  ready: boolean; // true after first real value
}

/* -----------------------------------------------------------------
   Hook: undo/redo + *debounced* history snapshots
   ----------------------------------------------------------------- */
export const useUndoableState = <T>(
  initial: T | null = null,
  { capacity = 100, debounceMs = 0 }: Options = {},
): UndoableState<NonNullable<T>> => {
  type S = NonNullable<T>;

  /* live value shown in the UI (may be newer than last snapshot) */
  const [live, setLive] = useState<S | undefined>(
    initial !== null ? (initial as S) : undefined,
  );

  /* time-travel history (never null) + current pointer */
  const historyRef = useRef<S[]>([]);
  const [idx, setIdx] = useState(-1); // -1 means “not ready”

  /* ---------------- internal helpers ---------------- */

  // push a *non-null* snapshot into history (trimming & rewinding)
  const pushSnapshot = useCallback(
    (value: S) => {
      setIdx((prev) => {
        historyRef.current = historyRef.current.slice(0, prev + 1); // drop “future”
        if (historyRef.current.length >= capacity) historyRef.current.shift();
        historyRef.current.push(value);
        return historyRef.current.length - 1;
      });
    },
    [capacity],
  );

  // flush any debounced change before nav/reset
  const flushPending = useCallback(() => {
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
      debounceTimer.current = null;
    }
    if (
      live !== undefined &&
      (idx === -1 || !Object.is(historyRef.current[idx], live))
    ) {
      pushSnapshot(live);
    }
  }, [idx, live, pushSnapshot]);

  /* ---------------- debounced SET ---------------- */

  const debounceTimer = useRef<NodeJS.Timeout | null>(null);

  const set: UndoableState<S>['set'] = useCallback(
    (update) => {
      setLive((prevRaw) => {
        const prev = prevRaw as S | undefined;
        const next =
          typeof update === 'function'
            ? (update as (p: S | undefined) => S)(prev)
            : update;

        // UI updates immediately…
        const schedule = () => pushSnapshot(next);

        if (debounceMs > 0) {
          if (debounceTimer.current) clearTimeout(debounceTimer.current);
          debounceTimer.current = setTimeout(schedule, debounceMs);
        } else {
          schedule();
        }
        return next;
      });
    },
    [debounceMs, pushSnapshot],
  );

  /* ---------------- undo / redo / reset ---------------- */

  const undo = useCallback(() => {
    flushPending();
    setIdx((i) => {
      const nextIdx = i > 0 ? i - 1 : i;
      setLive(historyRef.current[nextIdx]);
      return nextIdx;
    });
  }, [flushPending]);

  const redo = useCallback(() => {
    flushPending();
    setIdx((i) => {
      const nextIdx = i !== -1 && i < historyRef.current.length - 1 ? i + 1 : i;
      setLive(historyRef.current[nextIdx]);
      return nextIdx;
    });
  }, [flushPending]);

  const reset = useCallback(
    (nextInitial?: S) => {
      flushPending();
      historyRef.current = nextInitial == null ? [] : [nextInitial];
      setIdx(nextInitial == null ? -1 : 0);
      setLive(nextInitial);
    },
    [flushPending],
  );

  /* ---------- mount-time seeding if caller passed non-null ---------- */
  useEffect(() => {
    if (idx === -1 && initial !== null) pushSnapshot(initial as S);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    state: live as S,
    set,
    undo,
    redo,
    canUndo: idx > 0,
    canRedo: idx !== -1 && idx < historyRef.current.length - 1,
    reset,
    ready: live !== undefined,
  };
};
